import React from "react";
import { Link } from "react-router-dom";
import { Bed, Utensils, Users, BookOpen, Moon, ShieldCheck } from "lucide-react";
import { PageHero, Section, Card } from "@/components/Section";
import { IMAGES } from "@/lib/site";

const services = [
  { icon: Bed, t: "Chambres confortables", d: "Chambres de 2 à 4 places, meublées, lumineuses et calmes." },
  { icon: Utensils, t: "Restauration de qualité", d: "Petit-déjeuner, dîner et goûter préparés sur place, équilibrés et variés." },
  { icon: BookOpen, t: "Étude encadrée", d: "Temps d'étude du soir avec un éducateur, pour avancer sereinement dans son travail." },
  { icon: Users, t: "Vie collective", d: "Activités, jeux et sorties encadrées : un vrai cadre de vie en dehors des cours." },
  { icon: Moon, t: "Surveillance nuit & jour", d: "Une équipe éducative présente 24h/24 pour le bien-être de chacun." },
  { icon: ShieldCheck, t: "Cadre rassurant", d: "Règles claires, écoute attentive et lien régulier avec les familles." },
];

export default function Internat() {
  return (
    <div data-testid="internat-page">
      <PageHero
        eyebrow="Internat"
        title="Un internat à taille humaine pour grandir en sécurité"
        subtitle="Pour les jeunes éloignés de l'établissement ou qui ont besoin d'un cadre supplémentaire, notre internat partenaire offre un environnement chaleureux, encadré et propice à la réussite."
        image={IMAGES.internat}
      />

      <Section eyebrow="Nos services" title="Tout est pensé pour le bien-être des élèves">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((s, i) => (
            <Card key={s.t} data-testid={`internat-service-${i}`}>
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-sage/15 text-brand-sage-dark">
                <s.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-5 font-heading text-lg font-semibold text-brand-slate-dark">{s.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section wrapper="bg-brand-slate-dark text-brand-cream">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <div className="overflow-hidden rounded-3xl">
            <img src={IMAGES.campus} alt="Internat" className="h-full w-full object-cover" />
          </div>
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-brand-sand">
              Une journée à l'internat
            </p>
            <h2 className="mt-4 font-heading text-3xl font-bold sm:text-4xl">
              Un rythme régulier, des temps pour soi
            </h2>
            <ul className="mt-6 space-y-4 text-sm">
              {[
                ["7h00", "Réveil et petit-déjeuner"],
                ["8h00", "Cours au lycée"],
                ["17h30", "Goûter et temps libre"],
                ["18h30", "Étude encadrée"],
                ["19h45", "Dîner partagé"],
                ["21h30", "Temps calme et coucher"],
              ].map(([h, t]) => (
                <li key={h} className="flex items-start gap-4 border-b border-white/10 pb-3 last:border-none">
                  <span className="font-heading text-base font-bold text-brand-sand">{h}</span>
                  <span className="text-brand-cream/85">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      <Section>
        <div className="rounded-3xl bg-brand-sand-wrapper/60 p-10 text-center lg:p-16">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-brand-slate-dark sm:text-4xl">
            Visiter l'internat ou demander un dossier ?
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-muted-foreground">
            Notre équipe vous propose une visite personnalisée et répond à toutes
            vos questions sur le quotidien à l'internat.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/contact?type=inscription"
              data-testid="internat-cta-inscription"
              className="inline-flex items-center gap-2 rounded-full bg-brand-slate px-6 py-3.5 text-sm font-semibold text-brand-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-slate-dark"
            >
              Demander un dossier
            </Link>
            <Link
              to="/contact"
              data-testid="internat-cta-visit"
              className="inline-flex items-center gap-2 rounded-full border border-brand-slate/20 px-6 py-3.5 text-sm font-semibold text-brand-slate-dark transition-all duration-300 hover:bg-brand-slate hover:text-brand-cream"
            >
              Organiser une visite
            </Link>
          </div>
        </div>
      </Section>
    </div>
  );
}
